import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, Card, CardContent, Button
} from '@mui/material';
import { Payment } from '@mui/icons-material';

const Payments: React.FC = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [paymentData, setPaymentData] = useState({ method: 'Cash', status: 'Paid', notes: '' });
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    customDate: '',
    status: '',
    client: '',
    method: ''
  });
  const [sortBy, setSortBy] = useState<string>('appointmentDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, appointments, sortBy, sortOrder]);

  const applyFilters = () => {
    let filtered = appointments;

    if (filters.date && filters.date !== '') {
      if (filters.date === 'last7days') {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const today = new Date().toISOString().split('T')[0];
        filtered = filtered.filter(apt => {
          const aptDate = new Date(apt.appointmentDate).toISOString().split('T')[0];
          return aptDate >= sevenDaysAgo && aptDate <= today;
        });
      } else {
        const targetDate = filters.date === 'custom' ? filters.customDate : filters.date;
        if (targetDate) {
          filtered = filtered.filter(apt => 
            new Date(apt.appointmentDate).toISOString().split('T')[0] === targetDate
          );
        }
      }
    }

    if (filters.status) {
      filtered = filtered.filter(apt => 
        (apt.payment?.payment_status || 'pending').toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.client) {
      filtered = filtered.filter(apt => 
        `${apt.client?.firstName} ${apt.client?.lastName}`.toLowerCase().includes(filters.client.toLowerCase())
      );
    }

    if (filters.method) {
      filtered = filtered.filter(apt => 
        (apt.paymentMethod || '') === filters.method
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case 'appointmentDate':
          result = new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime();
          break;
        case 'clientName':
          result = `${a.client?.firstName} ${a.client?.lastName}`.localeCompare(`${b.client?.firstName} ${b.client?.lastName}`);
          break;
        case 'totalAmount':
          result = (parseFloat(a.totalPrice) + parseFloat(a.tipAmount || 0)) - (parseFloat(b.totalPrice) + parseFloat(b.tipAmount || 0));
          break;
        case 'paymentStatus':
          result = (a.payment?.payment_status || 'pending').localeCompare(b.payment?.payment_status || 'pending');
          break;
        case 'paymentMethod':
          result = (a.payment?.payment_method || '').localeCompare(b.payment?.payment_method || '');
          break;
        default:
          return 0;
      }
      return sortOrder === 'desc' ? -result : result;
    });
    
    setFilteredAppointments(sorted);
  };

  const fetchAppointments = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/appointments');
      if (response.ok) {
        const appointments = await response.json();
        
        // Fetch payment data for each appointment
        const appointmentsWithPayments = await Promise.all(
          appointments.map(async (apt: any) => {
            try {
              const paymentResponse = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${apt.id}/payment-info`);
              if (paymentResponse.ok) {
                const payment = await paymentResponse.json();
                return { ...apt, payment };
              }
            } catch (error) {
              console.error('Error fetching payment for appointment:', apt.id);
            }
            return { ...apt, payment: null };
          })
        );
        
        setAppointments(appointmentsWithPayments);
        setFilteredAppointments(appointmentsWithPayments);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handlePaymentUpdate = async () => {
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${selectedAppointment.id}/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethod: paymentData.method,
          paymentStatus: paymentData.status,
          paymentNotes: paymentData.notes,
          paymentDate: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        fetchAppointments();
        setIsPaymentDialogOpen(false);
        setSelectedAppointment(null);
        setPaymentData({ method: 'Cash', status: 'Paid', notes: '' });
      }
    } catch (error) {
      console.error('Error updating payment:', error);
    }
  };

  const handlePayment = (appointment: any) => {
    setSelectedAppointment(appointment);
    setPaymentData({
      method: appointment.paymentMethod || 'Cash',
      status: appointment.paymentStatus || 'Paid',
      notes: appointment.paymentNotes || ''
    });
    setIsPaymentDialogOpen(true);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Appointment Payments</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage payments for completed appointments
        </Typography>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Payments</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Date Filter"
              value={filters.date}
              onChange={(e) => setFilters({...filters, date: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Dates</MenuItem>
              <MenuItem value={new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]}>Yesterday</MenuItem>
              <MenuItem value={new Date().toISOString().split('T')[0]}>Today</MenuItem>
              <MenuItem value={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}>Tomorrow</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="custom">Select Date</MenuItem>
            </TextField>
          </Grid>
          {filters.date === 'custom' && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Select Date"
                type="date"
                value={filters.customDate || ''}
                onChange={(e) => setFilters({...filters, customDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Payment Status"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Client Name"
              value={filters.client}
              onChange={(e) => setFilters({...filters, client: e.target.value})}
              size="small"
              placeholder="Search by client name"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Payment Method"
              value={filters.method}
              onChange={(e) => setFilters({...filters, method: e.target.value})}
              size="small"
            >
              <MenuItem value="">All Methods</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Card">Credit/Debit Card</MenuItem>
              <MenuItem value="Online">Online Payment</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              size="small"
            >
              <MenuItem value="appointmentDate">Appointment Date</MenuItem>
              <MenuItem value="clientName">Client Name</MenuItem>
              <MenuItem value="totalAmount">Total Amount</MenuItem>
              <MenuItem value="paymentStatus">Payment Status</MenuItem>
              <MenuItem value="paymentMethod">Payment Method</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <TextField
              select
              fullWidth
              label="Order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              size="small"
            >
              <MenuItem value="desc">↓ Desc</MenuItem>
              <MenuItem value="asc">↑ Asc</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setFilters({ date: new Date().toISOString().split('T')[0], customDate: '', status: '', client: '', method: '' });
              setSortBy('appointmentDate');
              setSortOrder('desc');
            }}
            size="small"
          >
            Clear Filters
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', ml: 2, color: 'text.secondary' }}>
            Showing {filteredAppointments.length} of {appointments.length} payments
          </Typography>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {filteredAppointments.map((appointment) => (
          <Grid item xs={12} md={6} lg={4} key={appointment.id}>
            <Card sx={{
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              border: '1px solid #00BCD4',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 25px rgba(0, 188, 212, 0.2)' }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} color="#00BCD4">
                      💰 ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                    </Typography>
                    {appointment.tipAmount > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        (${appointment.totalPrice} + ${appointment.tipAmount} tip)
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={appointment.payment?.payment_status || 'pending'}
                    color={getPaymentStatusColor(appointment.payment?.payment_status || 'pending') as any}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="body1" fontWeight={600}>
                    👤 {appointment.client?.firstName} {appointment.client?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    📅 {new Date(appointment.appointmentDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    💅 with {appointment.primaryStaff?.firstName} {appointment.primaryStaff?.lastName}
                  </Typography>
                </Box>
                {appointment.payment && (
                  <Box mb={2} sx={{ p: 1, backgroundColor: '#E0F7FA', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Method: {appointment.payment.payment_method}
                    </Typography>
                    {appointment.payment.payment_date && (
                      <Typography variant="body2" color="text.secondary">
                        Date: {new Date(appointment.payment.payment_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}
                <Box display="flex" justifyContent="center" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Payment />}
                    onClick={() => handlePayment(appointment)}
                    disabled={appointment.status !== 'COMPLETED' && appointment.status !== 'CONFIRMED' && appointment.paymentStatus !== 'Pending'}
                    sx={{ backgroundColor: '#00BCD4' }}
                  >
                    {appointment.payment?.payment_status === 'paid' ? 'Update Payment' : 'Process Payment'}
                  </Button>
                  {appointment.payment?.payment_status === 'paid' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setPaymentData({ method: appointment.paymentMethod || 'Cash', status: 'Refunded', notes: 'Refund processed' });
                        setIsPaymentDialogOpen(true);
                      }}
                    >
                      Refund
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={isPaymentDialogOpen} onClose={() => setIsPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment for {selectedAppointment?.client?.firstName} {selectedAppointment?.client?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" color="#00BCD4" gutterBottom>
              Amount: ${selectedAppointment && (parseFloat(selectedAppointment.totalPrice) + parseFloat(selectedAppointment.tipAmount || 0)).toFixed(2)}
            </Typography>
            {selectedAppointment?.tipAmount > 0 && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Service: ${selectedAppointment.totalPrice} + Tip: ${selectedAppointment.tipAmount}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Date: {selectedAppointment && new Date(selectedAppointment.appointmentDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Payment Method"
                value={paymentData.method}
                onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
              >
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Card">Credit/Debit Card</MenuItem>
                <MenuItem value="Online">Online Payment</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Payment Status"
                value={paymentData.status}
                onChange={(e) => setPaymentData({...paymentData, status: e.target.value})}
              >
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Refunded">Refunded</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Payment Notes"
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                placeholder="Add any payment notes or transaction details..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePaymentUpdate} variant="contained" sx={{ backgroundColor: '#00BCD4' }}>
            Update Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
